import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";

interface PortalInvite {
  token: string;
  role: string;
  company: string;
  status: string;
  created_at: string;
  persona: string;
  score: string | null;
}

interface PortalHomeData {
  ok: boolean;
  email: string;
  first_name: string;
  invites: PortalInvite[];
}

const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  slate: "#7C7590",
  border: "#E8E4DC",
  white: "#FFFFFF",
  good: "#1A7A4A",
  warn: "#B5811F",
};

const STATUS_LABEL: Record<string, string> = {
  invited: "Ready to start",
  in_progress: "In progress",
  completed: "Completed",
  expired: "Expired",
  erased: "Data removed",
};

const STATUS_COLOR: Record<string, string> = {
  invited: T.accent,
  in_progress: T.warn,
  completed: T.good,
  expired: T.slate,
  erased: T.slate,
};

export function CandidatePortal() {
  const navigate = useNavigate();
  const [data, setData] = useState<PortalHomeData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<PortalHomeData>("/candidate/portal/home")
      .then(setData)
      .catch((err: Error & { status?: number }) => {
        if (err.status === 401) {
          navigate("/candidate/login", { replace: true });
        } else {
          setError(err.message ?? "Failed to load.");
        }
      });
  }, [navigate]);

  async function handleLogout() {
    try {
      await fetch("/candidate/logout", { redirect: "manual" });
    } catch {
      /* ignore */
    }
    navigate("/candidate/login", { replace: true });
  }

  if (error) {
    return (
      <div style={{ minHeight: "100dvh", background: T.paper, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: T.slate, fontSize: 14 }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ minHeight: "100dvh", background: T.paper, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: T.slate, fontSize: 14 }}>Loading…</p>
      </div>
    );
  }

  const activeInvites = data.invites.filter(
    (inv) => !["expired", "erased", "completed"].includes(inv.status)
  );
  const pastInvites = data.invites.filter((inv) =>
    ["expired", "erased", "completed"].includes(inv.status)
  );

  return (
    <div style={{ minHeight: "100dvh", background: T.paper }}>
      {/* Header */}
      <div style={{ padding: "36px 24px 28px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: T.accent }}>
              Your portal
            </p>
            <h1 style={{ margin: "6px 0 0", fontSize: 24, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
              Good {timeOfDay()}, {data.first_name}
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13.5, color: T.slate }}>{data.email}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <NotificationBell mode="candidate" />
            <button
              onClick={handleLogout}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 500,
                color: T.slate,
                background: "rgba(255,255,255,0.6)",
                border: `1px solid ${T.border}`,
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px 48px" }}>
        {data.invites.length === 0 ? (
          <div
            style={{
              background: T.white,
              borderRadius: 14,
              padding: "36px 24px",
              textAlign: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <p style={{ margin: 0, fontSize: 15, color: T.slate }}>
              No interview invitations yet. Check back when your consultant sends one.
            </p>
          </div>
        ) : (
          <>
            {activeInvites.length > 0 && (
              <section style={{ marginBottom: 28 }}>
                <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.slate }}>
                  Active
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {activeInvites.map((inv) => (
                    <InviteCard key={inv.token} inv={inv} />
                  ))}
                </div>
              </section>
            )}

            {pastInvites.length > 0 && (
              <section>
                <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.slate }}>
                  Past
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {pastInvites.map((inv) => (
                    <InviteCard key={inv.token} inv={inv} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InviteCard({ inv }: { inv: PortalInvite }) {
  const statusLabel = STATUS_LABEL[inv.status] ?? inv.status;
  const statusColor = STATUS_COLOR[inv.status] ?? T.slate;
  const isClickable = inv.status !== "erased";

  const inner = (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 12,
        padding: "16px 18px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        border: `1px solid ${T.border}`,
        transition: "box-shadow 0.15s",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {inv.role || "Interview session"}
          {inv.company ? ` · ${inv.company}` : ""}
        </p>
        <p style={{ margin: "3px 0 0", fontSize: 12.5, color: T.slate }}>
          {inv.persona || "Interview prep"}
        </p>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        {inv.score && (
          <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 700, color: T.good }}>{inv.score}</p>
        )}
        <span
          style={{
            display: "inline-block",
            padding: "3px 8px",
            borderRadius: 6,
            fontSize: 11.5,
            fontWeight: 600,
            color: statusColor,
            background: `${statusColor}18`,
          }}
        >
          {statusLabel}
        </span>
      </div>
      {isClickable && (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.slate} strokeWidth={2} style={{ flexShrink: 0 }}>
          <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );

  if (!isClickable) return inner;
  return (
    <Link to={`/invite/${inv.token}/home`} style={{ textDecoration: "none" }}>
      {inner}
    </Link>
  );
}

function timeOfDay(): string {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}
